import { EditorProps } from "@tiptap/pm/view";
import { Editor } from "@tiptap/react";
import { handleImageFileUpload, handleImageUrlPaste } from "./image-handlers";
import { toast } from "sonner";
import { MEMO_BYTE_LIMIT } from "../const/const";

const encoder = new TextEncoder();

interface CreateEditorPropsParams {
  editor: Editor;
  projectId?: string;
  fileName?: string;
  limitReached: () => boolean;
  onLimitExceeded: () => void;
  onImageClick: (id: number, url: string) => void;
}

export const createEditorProps = ({
  editor,
  projectId,
  fileName,
  onImageClick,
  limitReached,
  onLimitExceeded
}: CreateEditorPropsParams): EditorProps => {
  const nonBlockingKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "PageUp",
    "PageDown"
  ];
  const isAddingContent = (e: KeyboardEvent) => !nonBlockingKeys.includes(e.key) && !(e.ctrlKey || e.metaKey);

  let lastToastTs = 0;
  const showLimitToast = () => {
    const now = Date.now();
    if (now - lastToastTs > 3000) {
      toast.error("Memo size limit reached. Please delete some content before adding more.");
      lastToastTs = now;
      onLimitExceeded();
    }
  };
  const resetToastTimer = () => {
    if (!limitReached()) lastToastTs = 0;
  };

  return {
    handleKeyDown: (view, event) => {
      // ⌘/Ctrl+S → prevent browser save dialogue
      if ((event.metaKey || event.ctrlKey) && event.key === "s") {
        event.preventDefault();
        return true;
      }

      resetToastTimer();

      // If limit reached & user is adding content → block
      if (limitReached() && isAddingContent(event)) {
        event.preventDefault();
        showLimitToast();
        return true;
      }
      return false;
    },

    handlePaste: (view, event) => {
      const currentMarkdown = editor.storage.markdown.getMarkdown();
      const currentBytes = encoder.encode(currentMarkdown).length;

      const items = event.clipboardData?.items;
      if (!items || !projectId) return false;

      // Already beyond limit → nothing new allowed
      if (limitReached()) {
        event.preventDefault();
        showLimitToast();
        return true;
      }

      const blockPaste = () => {
        event.preventDefault();
        showLimitToast();
        return true;
      };

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type.startsWith("image/")) {
          if (currentBytes + 60 > MEMO_BYTE_LIMIT) {
            return blockPaste();
          }
          const file = item.getAsFile();
          if (file) {
            handleImageFileUpload(file, editor, projectId, fileName);
            return true;
          }
          continue;
        }

        if (item.type === "text/plain") {
          const text = event.clipboardData?.getData("text/plain") || "";
          const pasteBytes = encoder.encode(text).length;

          // plain text too big
          if (currentBytes + pasteBytes > MEMO_BYTE_LIMIT) {
            return blockPaste();
          }

          const imageUrlPattern = /^(https?:\/\/.*\.(?:png|jpg|jpeg|gif|webp|svg))$/i;
          if (imageUrlPattern.test(text.trim())) {
            if (currentBytes + 60 > MEMO_BYTE_LIMIT) {
              return blockPaste();
            }
            handleImageUrlPaste(text.trim(), editor);
            return true;
          }

          return false;
        }
      }

      return false;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleClickOn: (view, pos, node, nodePos, event, direct) => {
      if (node.type.name === "image") {
        const id = node.attrs.imageId;
        const url = node.attrs.src;

        onImageClick(id, url);
        return true;
      }
      return false;
    }
  };
};
